import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import { CheckCircle, XCircle } from "react-bootstrap-icons";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import AgreementForm from "../components/AgreementForm";
import Loader from "../components/Loader";
import NotFound from "../components/NotFound";
import ShootingStars from "../components/ShootingStars";
import { TextInput } from "../form-generator/components";
import { highlightError } from "../form-generator/helpers/utility";
import { makeRESTApiRequests } from "../helpers/api";
import { ENDPOINTS } from "../helpers/constants";

const Agreement = ({ preview = false }) => {
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState();
  const [disabledButton, setDisabledButton] = useState(false);
  const { uuid } = useParams();
  const [successAction, setSuccessAction] = useState("");
  const [error, setError] = useState("");
  const [action, setAction] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContractFromUuid();
  }, []);

  const fetchContractFromUuid = async () => {
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.GET_JOBS_FROM_UUID(uuid),
    });

    if (error) {
      setLoading(false);
      return toast.error(error);
    }

    if (response) {
      const details = response;
      setContract(details);
      setLoading(false);

      if (!preview && details["contractStatus"] === "Contract Sent")
        changeContractStatus("viewed");
    } else {
      setLoading(false);
      setError(response["reason"] || "Oops, something went wrong!");
    }
  };

  const changeContractStatus = async (status, license, phoneNumber) => {
    setSubmitting(true);

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.CONTRACT_ACTION(uuid),
      requestBody: {
        ...(status === "agreed"
          ? {
              [contract["isClientDifferent"]
                ? "clientPhone"
                : "phone"]: phoneNumber,
              driversLicense: license,
            }
          : undefined),
        contractStatus: status,
      },
    });

    if (error) {
      setSubmitting(false);
      if (status !== "viewed") {
        setDisabledButton(false);
        toast.error(error);
      }
      return;
    }

    setSubmitting(false);
    if (status !== "viewed") {
      if (response) {
        setSuccessAction(status);
      }
    }
  };

  const approveAgreement = async () => {
    /* const agreedDueDate = new Date(contract["Agreed Due Date"]);
    const today = new Date();

    if (agreedDueDate.getTime() - today.getTime() < 7 * 24 * 60 * 60 * 1000) {
      toast.error(
        "Agreed Due Date must be at least 7 days from date of agreement"
      );
      return;
    } */

    const requiredFields = ["driverLicenseFromModal", "phoneNumberFromModal"];

    for (const field of requiredFields) {
      const elem = document.getElementById(field);
      const value = elem.value;
      if (!value) {
        highlightError(elem);
        return;
      }
    }

    setDisabledButton(true);
    const license = document.getElementById("driverLicenseFromModal").value;
    const phoneNumber = document.getElementById("phoneNumberFromModal").value;

    await changeContractStatus("agreed", license, phoneNumber);
  };

  const rejectAgreement = async () => {
    setDisabledButton(true);
    await changeContractStatus("rejected");
  };

  const openInquiryChat = () => {
    const path = contract?.inquiryToken
      ? `/inquire/${contract.inquiryToken}`
      : "/inquire";
    window.open(path, "_blank", "noopener,noreferrer");
  };

  const SubmitSuccessLayout = () => {
    return (
      <Row className="justify-content-center text-center flex-grow-1">
        <Col xs={12} lg={7} className="px-3 align-self-center">
          {successAction === "agreed" ? (
            <CheckCircle className="text-success" size={60} />
          ) : (
            <XCircle className="text-danger" size={60} />
          )}
          <h2 className="my-4">
            <b>Thank You!</b>
          </h2>
          <h5>
            {successAction === "agreed"
              ? "Thank you for your approval. We will get back to you shortly"
              : "We are sorry we could not help you at this time. "}
          </h5>

          <Button
            variant="outline-primary"
            size="lg"
            className="mt-4 mr-2"
            onClick={openInquiryChat}
          >
            Inquire about this Job
          </Button>
          <a href="/">
            <Button className="text-white mt-4" variant="primary" size="lg">
              Back To Website
            </Button>
          </a>
        </Col>
      </Row>
    );
  };

  const handleClose = () => {
    setAction("");
  };

  const ConfirmationModal = () => {
    return (
      <Modal show={action} onHide={handleClose} centered backdrop="static">
        <Modal.Header>
          <Modal.Title>
            {action === "rejected"
              ? "Reject Order"
              : "Please confirm the following fields for accepting the order"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {action === "rejected" &&
            "Are you sure you want to reject the contract?"}
          {action === "agreed" && (
            <>
              <h6 className="my-2">Driver License</h6>
              <TextInput
                id="driverLicenseFromModal"
                required={true}
                value={contract["Driver License"]}
              />
              <h6 className="my-2">Phone number</h6>
              <TextInput
                className="mb-3"
                id="phoneNumberFromModal"
                required={true}
                value={
                  contract["Different Client"]
                    ? contract["Client Phone"]
                    : contract["Phone"]
                }
              />
              <div className="text-muted">
                By approving the order, you agree that you've read and agreed to
                the terms of the contract
              </div>
            </>
          )}
          {submitting && (
            <ProgressBar
              className="my-2"
              animated
              now={100}
              variant="primary"
              label="Please wait..."
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            disabled={submitting}
            variant="success"
            onClick={() => {
              if (action === "agreed") {
                approveAgreement();
              } else {
                rejectAgreement();
              }
            }}
          >
            {action === "agreed" ? "Approve" : "Reject"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <Container
      fluid
      className="w-100 overflow-hidden"
      style={{ height: "86%" }}
    >
      <Row className="h-100">
        <Col xs={12} className="h-100 p-4 d-flex flex-column overflow-auto">
          <hr className="w-100" />
          {successAction ? (
            <SubmitSuccessLayout />
          ) : (
            <>
              {loading && <Loader />}
              {contract && (
                <>
                  <AgreementForm contract={contract} />
                  <Row>
                    <Col xs={12} className="text-right">
                      <Button
                        variant="outline-primary"
                        className="mr-2"
                        onClick={openInquiryChat}
                      >
                        Inquire about this Job
                      </Button>
                      {!preview && (
                        <>
                          <Button
                            variant="danger"
                            className="mr-2"
                            onClick={() => setAction("rejected")}
                            disabled={disabledButton}
                          >
                            Reject
                          </Button>
                          <Button
                            disabled={disabledButton}
                            variant="success"
                            onClick={() => setAction("agreed")}
                          >
                            Approve
                          </Button>
                        </>
                      )}
                    </Col>
                    {!preview && ConfirmationModal()}
                  </Row>
                </>
              )}
            </>
          )}
          {error && <NotFound text={error} />}
        </Col>
      </Row>
    </Container>
  );
};

export default Agreement;
